/**
 * THE CORRECTION WORKFLOW — Request -> Verify -> Approve (desk, 2026-10-05).
 *
 * Who verifies and who approves is a SELECTION of named users made in Administration → Correction Workflow; this service reads
 * and saves it, tells the signed-in user what they can do (`me`), and fetches the ONE combined list the verifier / approver
 * screen works from. Plain `httpClient`/`unwrap` like the two correction services: no OpenAPI registration, so the generated
 * client's typed path map does not cover it.
 */
import { httpClient, unwrap, API_ENDPOINTS } from '@core/api';
import type { ApiSuccessResponse } from '@app-types/api.types';
import type {
  CorrectionApprovalDomain,
  CorrectionApprovalView,
  CorrectionDesignationsDto,
  CorrectionReceiptLookupDto,
  CorrectionRegisterDto,
  CorrectionRequestsDto,
  CorrectionWorkflowMeDto,
  PaymentCorrectionStatus,
  SetCorrectionDesignationsInput,
} from '@nexao/contracts';

const BASE = API_ENDPOINTS.financial.correctionWorkflow;

export async function getCorrectionDesignations(): Promise<CorrectionDesignationsDto> {
  return unwrap(httpClient.get<ApiSuccessResponse<CorrectionDesignationsDto>>(`${BASE}/designations`));
}

export async function setCorrectionDesignations(input: SetCorrectionDesignationsInput): Promise<CorrectionDesignationsDto> {
  return unwrap(httpClient.put<ApiSuccessResponse<CorrectionDesignationsDto>>(`${BASE}/designations`, input));
}

/** What the signed-in user can do, and how much is waiting for THEM. Safe for every user: it describes only themself. */
export async function getCorrectionWorkflowMe(): Promise<CorrectionWorkflowMeDto> {
  return unwrap(httpClient.get<ApiSuccessResponse<CorrectionWorkflowMeDto>>(`${BASE}/me`));
}

/** `mine: true` = the requests I entered (any user); without it = everything (selected verifiers / approvers and overseers only). */
export async function listCorrectionRequests(query: { status?: PaymentCorrectionStatus; mine?: boolean }): Promise<CorrectionRequestsDto> {
  return unwrap(
    httpClient.get<ApiSuccessResponse<CorrectionRequestsDto>>(`${BASE}/requests`, {
      params: { ...(query.status ? { status: query.status } : {}), ...(query.mine ? { mine: 'true' } : {}) },
    }),
  );
}

/**
 * THE MODIFICATIONS MODULE's list — verified requests of one domain (OP / IP), for a selected APPROVER only. The server refuses
 * anyone else with 403, whatever permissions they hold (a SuperAdmin included).
 */
export async function listCorrectionApprovals(query: { domain: CorrectionApprovalDomain; view: CorrectionApprovalView }): Promise<CorrectionRequestsDto> {
  return unwrap(httpClient.get<ApiSuccessResponse<CorrectionRequestsDto>>(`${BASE}/approvals`, { params: { domain: query.domain, view: query.view } }));
}

/**
 * THE CORRECTION TAB's first step — a typed receipt number resolved to the receipt, but ONLY when the signed-in user created it.
 * Another user's receipt comes back as `NOT_YOURS` with no receipt data in it (desk, 2026-10-12).
 */
export async function lookupCorrectionReceipt(receiptNumber: string): Promise<CorrectionReceiptLookupDto> {
  return unwrap(httpClient.get<ApiSuccessResponse<CorrectionReceiptLookupDto>>(`${BASE}/receipt-lookup`, { params: { receiptNumber } }));
}

export interface CorrectionRegisterFilters {
  fromDate?: string;
  toDate?: string;
  receiptNumber?: string;
  opNumber?: string;
  status?: PaymentCorrectionStatus;
}

/** The signed-in user's OWN correction requests, by date / receipt number / OP number. The server pins it to the caller. */
export async function listCorrectionRegister(filters: CorrectionRegisterFilters): Promise<CorrectionRegisterDto> {
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (typeof value === 'string' && value.trim() !== '') params[key] = value.trim();
  }
  return unwrap(httpClient.get<ApiSuccessResponse<CorrectionRegisterDto>>(`${BASE}/register`, { params }));
}
